import React, { useState, useEffect, useMemo } from 'react';
import LoaderOverlay from '../../components/LoaderOverlay';
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import FilterSection from './FilterSection';
import SearchBar from './SearchBar';
import ConceptosTable from './ConceptosTable';
import ViewConceptModal from './ViewConceptModal';
import NewConceptModal from './NewConceptModal';
import { getConceptoCobro, getConceptosCobro, getCycles } from '../../api/conceptosCobro';

function ConceptosCobroScreen() {
  const { isCollapsed } = useSidebar();
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedConcepto, setSelectedConcepto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conceptos, setConceptos] = useState([]);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);
  const [ciclosList, setCiclosList] = useState([]);
  const [selectedCiclo, setSelectedCiclo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // Estado para campus seleccionado
  const [selectedCampus, setSelectedCampus] = useState(() => localStorage.getItem('selectedCampus') || '');
  // Cargar ciclos escolares al montar
  useEffect(() => {
    const loadCiclos = async () => {
      try {
        const data = await getCycles();
        const ciclosData = Array.isArray(data) ? data : [];
        setCiclosList(ciclosData);
        // Establecer el ciclo más reciente como default
        if (ciclosData && ciclosData.length > 0) {
          const cicloMasReciente = ciclosData[0];
          setSelectedCiclo(cicloMasReciente.ciclo || cicloMasReciente.nombre_ciclo || '');
        }
      } catch (e) {
        console.error('Error cargando ciclos:', e);
        setCiclosList([]);
      }
    };
    loadCiclos();
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        // Construir parámetros de filtrado
        const params = {};
        if (selectedCiclo) {
          params.ciclo = selectedCiclo;
        }
        const data = await getConceptosCobro(params);
        if (mounted) setConceptos(Array.isArray(data) ? data : (data.results || []));
      } catch (e) {
        console.error('Error cargando conceptos de cobro:', e);
        if (mounted) setConceptos([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [refreshKey, selectedCiclo]);

  const handleConceptoCreated = (newConcepto) => {
    setRefreshKey(prev => prev + 1);
  };

  const handleView = async (conceptoId) => {
    try {
      setLoading(true);
      const concepto = await getConceptoCobro(conceptoId);
      setSelectedConcepto(concepto);
      setViewModalOpen(true);
    } catch (error) {
      console.error('Error al cargar concepto:', error);
      alert('Error al cargar el concepto');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (conceptoId) => {
    try {
      // No activar loader para editar, solo cargar el concepto
      const concepto = await getConceptoCobro(conceptoId);
      setSelectedConcepto(concepto);
      setEditModalOpen(true);
    } catch (error) {
      console.error('Error al cargar concepto:', error);
      alert('Error al cargar el concepto');
    }
  };

  const handleEditSave = (updatedConcepto) => {
    setRefreshKey(prev => prev + 1);
    setEditModalOpen(false);
    setSelectedConcepto(null);
    setIsDuplicateMode(false);
  };

  const handleDuplicate = async (conceptoId) => {
    try {
      const concepto = await getConceptoCobro(conceptoId);
      // Preparar datos para duplicar: limpiar el ID y código para forzar creación nueva
      const duplicateData = { ...concepto };
      delete duplicateData.id;
      delete duplicateData.codigo;
      delete duplicateData.codigo_concepto;
      delete duplicateData.created_at;
      delete duplicateData.updated_at;
      setSelectedConcepto(duplicateData);
      setIsDuplicateMode(true);
      setEditModalOpen(true);
    } catch (error) {
      console.error('Error al cargar concepto para duplicar:', error);
      alert('Error al cargar el concepto para duplicar');
    }
  };

  // Filtrar conceptos por búsqueda (código o nombre)
  const filteredConceptos = useMemo(() => {
    if (!searchQuery.trim()) return conceptos;
    const query = searchQuery.toLowerCase().trim();
    return conceptos.filter(concepto => {
      const codigo = (concepto.codigo || concepto.codigo_concepto || '').toLowerCase();
      const nombre = (concepto.nombre || concepto.nombre_concepto || '').toLowerCase();
      return codigo.includes(query) || nombre.includes(query);
    });
  }, [conceptos, searchQuery]);

  const handleSearch = () => {
    // La búsqueda se realiza en tiempo real con useMemo
    // Este handler puede usarse para búsqueda con botón si se requiere
  };
  
  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <HeaderGlobal
            title="Conceptos de Cobro"
            campus={selectedCampus}
            onCampusChange={e => setSelectedCampus(e.target.value)}
          />
          <main className={styles.mainContent}>
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} />
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={styles.programsSearchRow} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div className={estudiantesStyles.searchInputGroup} style={{ width: 160, marginRight: 8 }}>
                        <select 
                          className={estudiantesStyles.searchSelect} 
                          style={{ minWidth: 0, width: '100%', maxWidth: 160 }}
                          value={selectedCiclo}
                          onChange={(e) => setSelectedCiclo(e.target.value)}
                        >
                          <option value="">Todos los ciclos</option>
                          {ciclosList.map(ciclo => (
                            <option key={ciclo.id_ciclo_escolar || ciclo.id} value={ciclo.ciclo || ciclo.nombre_ciclo || ''}>
                              {ciclo.nombre_ciclo || ciclo.name || ciclo.ciclo || `${ciclo.anio}-${ciclo.numero_periodo}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={estudiantesStyles.searchInputGroup} style={{ flex: '1 1 0px', minWidth: 180, marginRight: 8 }}>
                        <svg
                          className={estudiantesStyles.searchIcon}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="#898d8d"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <input
                          type="text"
                          className={estudiantesStyles.searchInput}
                          placeholder="Buscar por código o nombre..."
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                        />
                      </div>
                      <button
                        className={styles.buscarButtonProgramas}
                        style={{ marginRight: 8 }}
                        onClick={handleSearch}
                      >
                        Buscar
                      </button>
                      <button
                        className={styles.newProgramButton}
                        onClick={() => setEditModalOpen(true)}
                        style={{ minWidth: 180 }}
                      >
                        + Nuevo Concepto
                      </button>
                    </div>
                  </div>
                </div>
                <ConceptosTable
                  conceptos={filteredConceptos}
                  loading={loading}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  onReload={() => setRefreshKey(prev => prev + 1)}
                  campus={selectedCampus}
                />
              </div>
            </div>
          </main>
        </div>
      </div>

      <ViewConceptModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        concepto={selectedConcepto}
      />

      <NewConceptModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedConcepto(null);
          setIsDuplicateMode(false);
        }}
        onSave={handleEditSave}
        editMode={!isDuplicateMode && !!selectedConcepto}
        duplicateMode={isDuplicateMode}
        conceptoData={selectedConcepto}
      />
    </div>
  );
}

export default ConceptosCobroScreen;
